import React from 'react';
import { useNavigate, Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <div className="bg-white border-b border-grey-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 text-primary-600 hover:text-primary-700 flex items-center gap-2 font-medium"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-grey-900">Privacy Policy</h1>
          <p className="text-grey-600 mt-2">Effective Date: January 1, 2025</p>
          <p className="text-grey-500 text-sm mt-1">Last Updated: November 27, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="prose prose-sm max-w-none text-grey-700 space-y-8">

            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">1. Introduction</h2>
              <p>
                Pebble The App, Inc. ("Pebble," "we," "us," or "our"), a Delaware corporation, is committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
                our platform, website, mobile applications, and related services (collectively, the "Service").
              </p>
              <p className="mt-3">
                By accessing or using the Service, you consent to the practices described in this Privacy Policy. If you 
                do not agree with our policies and practices, please do not use the Service.
              </p>
              <p className="mt-3">
                This policy applies to all users, including Event Organizers, Marketplace Vendors/Sellers, and visitors. 
                We may update this policy from time to time, and will notify you of material changes via email or prominent 
                notice on the Service.
              </p>
              <p className="mt-3">
                <strong>Delaware Residents:</strong> This policy is designed to comply with the Delaware Personal Data Privacy Act 
                (6 Del. C. § 12D-101 et seq.), effective January 1, 2025. See Section 10 for your specific rights under Delaware law.
              </p>
            </section>

            {/* Data Controller */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">2. Data Controller</h2>
              <p>
                For the purposes of applicable data protection laws, including the General Data Protection Regulation (GDPR), 
                the Delaware Personal Data Privacy Act (DPDPA), and the California Consumer Privacy Act (CCPA), 
                Pebble The App, Inc. is the data controller responsible for your personal data collected through the Service.
              </p>
              <div className="mt-3 p-4 bg-grey-50 rounded-lg">
                <p><strong>Pebble The App, Inc.</strong></p>
                <p className="mt-1">A Delaware Corporation</p>
                <p className="mt-2">Email: privacy@pebbletheapp.com</p>
                <p>Data Protection Inquiries: dpo@pebbletheapp.com</p>
              </div>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">3. Information We Collect</h2>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">3.1 Information You Provide Directly</h3>
              <p>We collect information you voluntarily provide when using the Service:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Account Information:</strong> Name, email address, password, phone number, profile photo</li>
                <li><strong>Business Profile:</strong> Business name, description, category, address, website, social media links</li>
                <li><strong>Event Information:</strong> Event name, description, date, location, pricing, images, vendor requirements</li>
                <li><strong>Payment Information:</strong> Billing address, payment card details (processed by Stripe), bank account information for payouts</li>
                <li><strong>Identity Verification:</strong> Government-issued ID, business licenses, tax identification numbers (when required)</li>
                <li><strong>Communications:</strong> Messages, support requests, feedback, and survey responses</li>
                <li><strong>Documents and Signatures:</strong> Contracts, waivers, permits, insurance certificates, compliance documents, electronic signatures, and document acknowledgments</li>
                <li><strong>Vendor Documents:</strong> Insurance certificates, food permits, business licenses, health permits, tax documents, and other compliance documentation you upload</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">3.2 Information Collected Automatically</h3>
              <p>When you access the Service, we automatically collect certain technical information:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Device Information:</strong> Device type, operating system, browser type, unique device identifiers</li>
                <li><strong>Usage Information:</strong> Pages viewed, features used, time spent, click patterns, search queries</li>
                <li><strong>Log Data:</strong> IP address, access times, referring URLs, error logs</li>
                <li><strong>Location Information:</strong> General location based on IP address; precise location only if you grant explicit permission</li>
                <li><strong>Cookies and Tracking Technologies:</strong> See Section 9 for details</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">3.3 Information from Third Parties</h3>
              <p>We may receive information about you from third-party sources:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Authentication Providers:</strong> If you sign in via Google, we receive your name, email, and profile photo</li>
                <li><strong>Payment Processors:</strong> Stripe provides transaction data and payment verification status</li>
                <li><strong>Business Verification:</strong> Identity verification services and business registries</li>
                <li><strong>Analytics Providers:</strong> Aggregated usage data and trends</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">3.4 Sensitive Personal Data</h3>
              <p>
                Under the Delaware Personal Data Privacy Act, "sensitive data" includes certain categories that require explicit consent 
                for processing. We generally do not collect sensitive data as defined by DPDPA, including:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Racial or ethnic origin</li>
                <li>Religious beliefs</li>
                <li>Mental or physical health conditions</li>
                <li>Sexual orientation or sex life</li>
                <li>Status as transgender or nonbinary</li>
                <li>Citizenship or immigration status</li>
                <li>Genetic or biometric data for identification purposes</li>
              </ul>
              <p className="mt-3">
                If we ever need to process sensitive data, we will obtain your explicit opt-in consent beforehand.
              </p>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">4. How We Use Your Information</h2>
              <p>We use your personal data for the following purposes:</p>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">4.1 Service Delivery</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Creating and managing your account</li>
                <li>Processing event registrations and vendor applications</li>
                <li>Facilitating payments between Event Organizers and Vendors</li>
                <li>Sending transactional emails (confirmations, receipts, notifications)</li>
                <li>Providing customer support</li>
                <li>Managing document uploads, acknowledgments, and electronic signatures</li>
                <li>Facilitating communication between Event Organizers and Vendors</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">4.2 Service Improvement</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Analyzing usage patterns to improve features and user experience</li>
                <li>Conducting research and developing new products</li>
                <li>Personalizing content and recommendations</li>
                <li>Testing and troubleshooting new features</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">4.3 Safety and Security</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Detecting and preventing fraud, abuse, and security incidents</li>
                <li>Verifying user identity and business legitimacy</li>
                <li>Enforcing our Terms of Use and policies</li>
                <li>Protecting the rights and safety of users and third parties</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">4.4 Marketing and Communications</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Sending promotional emails about new features, events, and offers (with your consent)</li>
                <li>Displaying relevant advertisements (where applicable)</li>
                <li>Conducting surveys and gathering feedback</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">4.5 Legal Compliance</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Complying with applicable laws, regulations, and legal processes</li>
                <li>Responding to law enforcement requests and legal claims</li>
                <li>Tax reporting and compliance</li>
                <li>Maintaining records of electronic signatures and document acknowledgments as required by law</li>
              </ul>
            </section>

            {/* Legal Basis for Processing */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">5. Legal Basis for Processing</h2>
              <p>
                We process your personal data based on the following legal grounds, as applicable under the GDPR, 
                Delaware DPDPA, and other privacy laws:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Contract Performance:</strong> Processing necessary to provide the Service you requested</li>
                <li><strong>Legitimate Interests:</strong> Processing for our legitimate business interests, such as fraud prevention, security, analytics, and service improvement, where not overridden by your rights</li>
                <li><strong>Consent:</strong> Processing based on your explicit consent, such as marketing communications or sensitive data processing</li>
                <li><strong>Legal Obligation:</strong> Processing required to comply with applicable laws</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">6. How We Share Your Information</h2>
              <p>We may share your personal data in the following circumstances:</p>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.1 With Other Users</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Event Organizers can view Vendor applications, business profiles, and uploaded compliance documents</li>
                <li>Vendors can view Event Organizer information for events they register for</li>
                <li>Public event listings may be visible to all visitors</li>
                <li>Document acknowledgment and signature records are shared with the relevant Event Organizer</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.2 With Service Providers</h3>
              <p className="mb-2">We share data with third-party vendors who assist in operating the Service:</p>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-grey-200 rounded-lg text-sm">
                  <thead className="bg-grey-50">
                    <tr>
                      <th className="px-4 py-2 text-left border-b">Provider</th>
                      <th className="px-4 py-2 text-left border-b">Purpose</th>
                      <th className="px-4 py-2 text-left border-b">Data Shared</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border-b">Stripe</td>
                      <td className="px-4 py-2 border-b">Payment processing</td>
                      <td className="px-4 py-2 border-b">Payment details, billing address, transaction data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">Firebase (Google)</td>
                      <td className="px-4 py-2 border-b">Database, authentication, hosting, file storage</td>
                      <td className="px-4 py-2 border-b">Account data, content, documents, usage logs</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">SendGrid (Twilio)</td>
                      <td className="px-4 py-2 border-b">Email notifications</td>
                      <td className="px-4 py-2 border-b">Email address, name, email content</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">Google Analytics</td>
                      <td className="px-4 py-2 border-b">Usage analytics</td>
                      <td className="px-4 py-2 border-b">Device info, usage data (anonymized)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">OpenAI</td>
                      <td className="px-4 py-2">AI-powered features</td>
                      <td className="px-4 py-2">Event descriptions, user queries (for AI assistance)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-sm">
                All service providers are contractually obligated to protect your data and may only use it for the 
                purposes specified in our agreements with them.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.3 For Legal Reasons</h3>
              <p>We may disclose information when required or permitted by law:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>To comply with legal obligations, court orders, or government requests</li>
                <li>To enforce our Terms of Use and other agreements</li>
                <li>To protect the safety, rights, or property of Pebble, users, or the public</li>
                <li>In connection with investigations of fraud, illegal activity, or security incidents</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.4 Business Transfers</h3>
              <p>
                If Pebble is involved in a merger, acquisition, sale of assets, or bankruptcy, your personal data may be 
                transferred as part of that transaction. We will notify you of any change in ownership or control.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.5 With Your Consent</h3>
              <p>We may share your information for other purposes with your explicit consent.</p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">6.6 No Sale of Personal Data</h3>
              <p>
                <strong>We do not sell your personal data.</strong> We do not engage in the sale of personal information 
                as defined by the Delaware Personal Data Privacy Act, California Consumer Privacy Act, or similar laws.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">7. Data Retention</h2>
              <p>
                We retain your personal data for as long as necessary to fulfill the purposes described in this Privacy Policy, 
                unless a longer retention period is required or permitted by law.
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Account Data:</strong> Retained while your account is active and for 3 years after deletion</li>
                <li><strong>Transaction Records:</strong> Retained for 7 years for tax and legal compliance</li>
                <li><strong>Event Data:</strong> Retained for 2 years after the event date</li>
                <li><strong>Documents and Signatures:</strong> Electronic signatures and document acknowledgments are retained for 7 years for legal compliance</li>
                <li><strong>Vendor Compliance Documents:</strong> Retained while you maintain an active account and for 3 years after account deletion</li>
                <li><strong>Marketing Data:</strong> Until you unsubscribe or withdraw consent</li>
                <li><strong>Log Data:</strong> Retained for 12 months</li>
              </ul>
              <p className="mt-3">
                When data is no longer needed, we securely delete or anonymize it. Anonymized data may be retained indefinitely 
                for analytics and research purposes.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">8. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data against unauthorized 
                access, alteration, disclosure, or destruction.
              </p>
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">Security Measures Include:</h3>
              <ul className="list-disc list-inside space-y-2">
                <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                <li>Secure authentication mechanisms</li>
                <li>Access controls and least-privilege principles</li>
                <li>Regular security assessments and monitoring</li>
                <li>Secure hosting infrastructure (Google Cloud/Firebase)</li>
                <li>PCI-DSS compliant payment processing (via Stripe)</li>
                <li>Employee security training and confidentiality agreements</li>
                <li>Secure document storage with access controls</li>
              </ul>
              <p className="mt-3">
                <strong>Important:</strong> No method of transmission over the Internet or electronic storage is 100% secure. 
                While we strive to protect your personal data, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">9. Cookies and Tracking Technologies</h2>
              <p>We use cookies and similar technologies to enhance your experience on the Service:</p>
              
              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.1 Types of Cookies We Use</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security, preferences)</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service</li>
                <li><strong>Functional Cookies:</strong> Remember your settings and preferences</li>
                <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (where applicable)</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.2 Managing Cookies</h3>
              <p>
                You can control cookies through your browser settings. Disabling certain cookies may affect the functionality 
                of the Service. Most browsers allow you to:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>View and delete existing cookies</li>
                <li>Block third-party cookies</li>
                <li>Block all cookies from specific sites</li>
                <li>Clear all cookies when you close your browser</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">9.3 Universal Opt-Out Mechanisms</h3>
              <p>
                Effective January 1, 2026, we will recognize and honor Global Privacy Control (GPC) signals and similar 
                universal opt-out mechanisms as required by the Delaware Personal Data Privacy Act. When we detect a valid 
                opt-out signal, we will treat it as a request to opt out of any targeted advertising or data sales.
              </p>
            </section>

            {/* Delaware Privacy Rights */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">10. Delaware Privacy Rights (DPDPA)</h2>
              <p>
                If you are a Delaware resident, you have specific rights under the Delaware Personal Data Privacy Act 
                (6 Del. C. § 12D-101 et seq.), effective January 1, 2025:
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">10.1 Your Rights</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Right to Access:</strong> Confirm whether we are processing your personal data and access that data</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal data</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal data</li>
                <li><strong>Right to Data Portability:</strong> Obtain your data in a portable, readily usable format</li>
                <li><strong>Right to Opt-Out:</strong> Opt out of:
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Sale of personal data (we do not sell your data)</li>
                    <li>Targeted advertising</li>
                    <li>Profiling for decisions with legal or significant effects</li>
                  </ul>
                </li>
                <li><strong>Right to Third-Party List:</strong> Obtain a list of categories of third parties to whom we have disclosed your personal data</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">10.2 How to Exercise Your Rights</h3>
              <p>To exercise your Delaware privacy rights:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Email: privacy@pebbletheapp.com</li>
                <li>Include "Delaware Privacy Request" in the subject line</li>
                <li>Provide sufficient information for us to verify your identity</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">10.3 Response Timeline</h3>
              <p>
                We will respond to your request within 45 days. If we need additional time due to complexity, we will notify 
                you and may extend by an additional 45 days. If we deny your request, you may appeal by emailing 
                privacy@pebbletheapp.com with "Delaware Privacy Appeal" in the subject line.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">10.4 Complaints</h3>
              <p>
                If we do not resolve your appeal satisfactorily, you may file a complaint with the Delaware Department of 
                Justice at <a href="https://attorneygeneral.delaware.gov/fraud/personal-data-privacy-portal/" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">attorneygeneral.delaware.gov</a>.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">10.5 Non-Discrimination</h3>
              <p>
                We will not discriminate against you for exercising your privacy rights. We will not deny you goods or services, 
                charge different prices, or provide a different quality of service because you exercised your rights.
              </p>
            </section>

            {/* Other State Privacy Rights */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">11. Additional State Privacy Rights</h2>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">11.1 California Residents (CCPA/CPRA)</h3>
              <p>California residents have additional rights under the California Consumer Privacy Act:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Right to Know:</strong> Request information about data collection, use, and disclosure practices</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
                <li><strong>Right to Opt-Out of Sale/Sharing:</strong> We do not sell your personal information</li>
                <li><strong>Right to Limit Use of Sensitive Personal Information:</strong> Limit use of sensitive data categories</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
              </ul>
              <p className="mt-3">
                <strong>California "Shine the Light" Law:</strong> California residents may request information about disclosure 
                of personal data to third parties for direct marketing purposes. We do not share personal data with third parties 
                for their direct marketing purposes without your consent.
              </p>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">11.2 GDPR Rights (EEA, UK, Switzerland)</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you</li>
                <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or for direct marketing</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
              </ul>

              <h3 className="font-semibold text-grey-800 mt-4 mb-2">11.3 How to Exercise Your Rights</h3>
              <p>To exercise any of these rights, please contact us:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Email: privacy@pebbletheapp.com</li>
                <li>Through your account settings (for certain requests)</li>
              </ul>
              <p className="mt-3">
                We will respond to your request within 30-45 days (or as required by applicable law). We may need to verify your 
                identity before processing certain requests. If we cannot fulfill your request, we will explain why.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">12. Children's Privacy</h2>
              <p>
                The Service is not intended for children under 18 years of age. We do not knowingly collect personal data from 
                children under 18.
              </p>
              <p className="mt-3">
                <strong>Special Protections for Minors (Delaware DPDPA):</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>For children under 13: We require verifiable parental consent before any processing, as required by COPPA</li>
                <li>For children ages 13-17: We require consent before processing for targeted advertising or sale of personal data</li>
              </ul>
              <p className="mt-3">
                If you are a parent or guardian and believe your child has provided us with personal data, 
                please contact us immediately at privacy@pebbletheapp.com. If we discover that we have collected 
                personal data from a child in violation of applicable law, we will promptly delete such information.
              </p>
            </section>

            {/* International Data Transfers */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">13. International Data Transfers</h2>
              <p>
                Pebble is based in the United States, and your personal data may be processed in the United States or other 
                countries where our service providers operate.
              </p>
              <p className="mt-3">
                If you are located in the EEA, UK, or Switzerland, we ensure appropriate safeguards are in place for international 
                data transfers, including:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Data processing agreements with our service providers</li>
                <li>Adequacy decisions where applicable</li>
              </ul>
            </section>

            {/* Data Breach Notification */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">14. Data Breach Notification</h2>
              <p>
                In the event of a data breach affecting your personal data, we will:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Notify affected users without undue delay (within 72 hours where required by GDPR)</li>
                <li>Report to relevant supervisory authorities as required by law, including the Delaware Department of Justice where applicable</li>
                <li>Take immediate steps to contain and remediate the breach</li>
                <li>Provide information about the nature of the breach and steps you can take</li>
              </ul>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">15. Third-Party Websites and Services</h2>
              <p>
                The Service may contain links to third-party websites, applications, or services not operated by us. This Privacy 
                Policy does not apply to those third parties. We encourage you to review the privacy policies of any third-party 
                services you access through the Service.
              </p>
              <p className="mt-3">
                We are not responsible for the privacy practices or content of third-party websites, even if linked from the Service.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">16. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes by:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li>Posting the updated policy on the Service with a new "Effective Date"</li>
                <li>Sending an email notification to registered users (for material changes)</li>
                <li>Displaying a prominent notice on the Service</li>
              </ul>
              <p className="mt-3">
                Your continued use of the Service after changes become effective constitutes acceptance of the revised Privacy Policy. 
                We encourage you to review this policy periodically.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-grey-900 mb-4">17. Contact Us</h2>
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="mt-3 p-4 bg-grey-50 rounded-lg space-y-2">
                <p><strong>Pebble The App, Inc.</strong></p>
                <p>A Delaware Corporation</p>
                <p className="mt-2">Privacy Inquiries: privacy@pebbletheapp.com</p>
                <p>Data Protection Officer: dpo@pebbletheapp.com</p>
                <p>General Support: support@pebbletheapp.com</p>
              </div>
              <p className="mt-3">
                <strong>For Delaware Residents:</strong> If we do not adequately resolve your privacy concerns, you may file a 
                complaint with the Delaware Department of Justice at 
                <a href="https://attorneygeneral.delaware.gov/fraud/personal-data-privacy-portal/" className="text-primary-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                  attorneygeneral.delaware.gov/fraud/personal-data-privacy-portal/
                </a>
              </p>
              <p className="mt-3">
                <strong>For EEA/UK Residents:</strong> You have the right to lodge a complaint with your local data protection 
                supervisory authority if you believe we have not addressed your concerns adequately.
              </p>
            </section>

            {/* Related Documents */}
            <section className="border-t border-grey-200 pt-6">
              <h2 className="text-xl font-semibold text-grey-900 mb-4">Related Documents</h2>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms-of-use" className="text-primary-600 hover:underline">Terms of Use</Link>
                </li>
              </ul>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
